/**
 * Format offer for GTM Enhanced Ecommerce
 * @param {Object} offer - The offer object from tunnel
 * @param {string} clubName - The club name for item_brand
 * @returns {Object|null} Formatted item for ecommerce
 */
export const formatOfferForEcommerce = (offer, clubName) => {
  if (!offer) return null;

  return {
    item_id: offer['@id'] || offer.offer?.product,
    item_name: offer.name,
    item_category: offer.type || 'subscription',
    item_brand: clubName,
    price: offer.offer?.priceTI ? (offer.offer.priceTI / 100) : 0,
    quantity: 1
  };
};

/**
 * Format options array for GTM Enhanced Ecommerce
 * @param {Array} options - The options array from tunnel
 * @param {string} clubName - The club name for item_brand
 * @returns {Array} Formatted items for ecommerce
 */
export const formatOptionsForEcommerce = (options, clubName) => {
  if (!options || !options.length) return [];

  return options.map((option, index) => ({
    item_id: option['@id'] || option.offer?.product,
    item_name: option.name,
    item_category: 'option',
    item_brand: clubName,
    price: option.offer?.priceTI ? (option.offer.priceTI / 100) : 0,
    quantity: 1,
    index: index
  }));
};

/**
 * Format regulars (equipment) for GTM Enhanced Ecommerce
 * @param {Object} regulars - The regulars object from tunnel
 * @param {string} clubName - The club name for item_brand
 * @returns {Array} Formatted items for ecommerce
 */
export const formatRegularsForEcommerce = (regulars, clubName) => {
  if (!regulars) return [];

  return Object.values(regulars)
    .filter(r => r.count > 0)
    .map(({ regular, count }) => ({
      item_id: regular['@id'],
      item_name: regular.name,
      item_category: 'equipment',
      item_brand: clubName,
      price: regular.offer?.priceTI ? (regular.offer.priceTI / 100) : 0,
      quantity: count
    }));
};

/**
 * Format full cart for purchase event
 * @param {Object} tunnel - The tunnel context
 * @returns {Array} All items formatted for ecommerce
 */
export const formatCartForEcommerce = (tunnel) => {
  const items = [];
  const clubName = tunnel.club?.name || '';

  // Add main offer
  if (tunnel.offer) {
    const offerItem = formatOfferForEcommerce(tunnel.offer, clubName);
    if (offerItem) items.push(offerItem);
  }

  // Add options
  if (tunnel.options?.length) {
    items.push(...formatOptionsForEcommerce(tunnel.options, clubName));
  }

  // Add regulars (equipment)
  if (tunnel.regulars) {
    items.push(...formatRegularsForEcommerce(tunnel.regulars, clubName));
  }

  return items;
};

/**
 * Calculate total value from tunnel sales
 * @param {Object} tunnel - The tunnel context
 * @returns {number} Total value in euros
 */
export const getTotalValue = (tunnel) => {
  return tunnel.sales?.totalTI ? (tunnel.sales.totalTI / 100) : 0;
};
